// ---------------------------------------------------------------------------
// Desktop keyboard routing.
//
// While the mode is 'answer' every keystroke belongs to the answer and the chat
// input must not be focused at all. Taking chat has to be deliberate: a player
// who loses a round's typing into a chat box does not come back.
//
// The mode is a fact the screens read — it is how a game hotkey knows not to
// fire mid-word — and nothing more. It is never announced to the player. The
// panel that owns the field also drops DOM focus when the mode leaves 'chat';
// this hook only ever moves the flag.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { isEditable } from '../lib/isEditable.js';

export type KeyboardMode = 'answer' | 'chat';

export interface KeyboardModeApi {
  mode: KeyboardMode;
  takeChat: () => void;
  releaseChat: () => void;
}


export function useKeyboardMode(): KeyboardModeApi {
  const [mode, setMode] = useState<KeyboardMode>('answer');

  const takeChat = useCallback(() => setMode('chat'), []);
  const releaseChat = useCallback(() => setMode('answer'), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape releases chat from anywhere, including from inside the chat
      // input, which is where it will almost always be pressed.
      if (event.key === 'Escape') {
        setMode('answer');
        return;
      }
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      // A numbers expression contains a legitimate '/', so the shortcut only
      // fires when nothing editable holds focus. Inside a field, the slash is
      // the player's, not ours.
      if (isEditable(event.target)) return;
      event.preventDefault();
      setMode('chat');
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return { mode, takeChat, releaseChat };
}
